import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "./api";
import SeverityBadge from "./SeverityBadge";
import { TIERS } from "./constants";
import CapabilityBadges, { CAPABILITY_LABELS } from "@/components/brand/CapabilityBadges";
import { Time } from "./time";

// Everything shown here is untrusted text rendered as React text nodes (escaped).
const PAGE_SIZE = 50;
const SEVERITIES = ["", "threat", "warning", "info", "none"];
const LLM = ["", "malicious", "suspicious", "benign", "unsure", "none"];
const LLM_COLORS = { malicious: "var(--hs-danger)", suspicious: "var(--hs-warning)", benign: "var(--hs-safe)", unsure: "var(--hs-text-muted)" };
const EMPTY = { q: "", tier: "", severity: "", rule: "", llm: "", publisher: "", human: "", dateFrom: "", dateTo: "", sort: "last_seen" };
// Drill-down filters (from the Stats page and elsewhere). URL param name = API param name.
export const EXTRA_FILTERS = {
  type: "type", source_id: "source", secret_name: "secret name", secret_type: "secret type",
  requires_secrets: "requires secrets", secret_in_conversation: "secret in conversation", remote_http: "remote over plain http",
  analysed: "analysed with current rules", watched: "watched package", lookalike: "typosquat candidate",
  multi_directory: "listed in 2+ directories", listed_in: "listed in", payment_type: "payment authorization",
  capability: "broad access", reviewed_by_me: "reviewed by me", has_my_note: "has my note",
  scan_status: "scan status",
};
const TYPES = ["", "mcp_server", "package", "mcp_remote", "manual"];

// Every filter lives in the URL (?q=…&severity=…&secret_name=…) so a view can be bookmarked or linked to.
export function filtersFromParams(sp) {
  const f = { ...EMPTY };
  f.q = sp.get("q") || ""; f.tier = sp.get("tier") || ""; f.severity = sp.get("severity") || "";
  f.rule = sp.get("rule") || ""; f.llm = sp.get("llm") || ""; f.publisher = sp.get("publisher") || "";
  const h = sp.get("has_human"); f.human = h === "true" ? "yes" : h === "false" ? "no" : "";
  f.dateFrom = sp.get("date_from") || ""; f.dateTo = sp.get("date_to") || ""; f.sort = sp.get("sort") || "last_seen";
  Object.keys(EXTRA_FILTERS).forEach((k) => { f[k] = sp.get(k) || ""; });
  return f;
}

export function buildQuery(f, cursor) {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.tier) p.set("tier", f.tier);
  if (f.severity) p.set("severity", f.severity);
  if (f.rule) p.set("rule", f.rule);
  if (f.llm) p.set("llm", f.llm);
  if (f.publisher) p.set("publisher", f.publisher.trim().toLowerCase());
  if (f.human) p.set("has_human", f.human === "yes" ? "true" : "false");
  if (f.dateFrom) p.set("date_from", f.dateFrom);
  if (f.dateTo) p.set("date_to", f.dateTo);
  if (f.sort && f.sort !== "last_seen") p.set("sort", f.sort);
  Object.keys(EXTRA_FILTERS).forEach((k) => { if (f[k]) p.set(k, f[k]); });
  if (cursor != null) { p.set("limit", String(PAGE_SIZE)); p.set("cursor", String(cursor)); }
  return p.toString();
}

function Excerpt({ m }) {
  return (
    <div style={{ fontFamily: "var(--hs-font-mono)", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "var(--hs-code-bg)",
      padding: 4, marginTop: 3 }} data-testid="brief-excerpt">
      <span style={{ color: "var(--hs-text-muted)" }}>{m.before}</span>
      <mark data-testid="brief-hl" className="hs-mark">{m.match}</mark>
      <span style={{ color: "var(--hs-text-muted)" }}>{m.after}</span>
    </div>
  );
}

function Brief({ brief }) {
  if (!brief) return <div style={{ color: "var(--hs-text-muted)" }}>Loading…</div>;
  return (
    <div data-testid="brief" style={{ fontSize: 12 }}>
      {brief.content_missing && <div style={{ color: "var(--hs-danger)" }}>Content missing from storage; showing what was stored with the verdict.</div>}
      {brief.rules.length === 0 && <div style={{ color: "var(--hs-text-muted)" }}>No signals recorded.</div>}
      {brief.rules.map((r) => (
        <div key={r.rule_id} style={{ marginBottom: 6 }}>
          <SeverityBadge severity={r.severity} /> <code>{r.rule_id}</code>
          {r.severity === "threat" && <span style={{ color: "var(--hs-text-muted)" }}> · weight {r.weight}</span>}
          {(r.matches || []).slice(0, 3).map((m, i) => <Excerpt key={i} m={m} />)}
        </div>))}
      {brief.requires_secrets.length > 0 && (
        <div style={{ marginTop: 4 }} data-testid="brief-secrets">Needs secrets: {brief.requires_secrets.map((s) => `${s.name} (${s.type})`).join(", ")}</div>)}
      <div style={{ marginTop: 6 }} data-testid="brief-llm">
        {brief.model
          ? <><strong style={{ color: LLM_COLORS[brief.model.label] }}>Claude: {brief.model.label}</strong>{" "}
              <span style={{ color: "var(--hs-text-muted)" }}>({brief.model.confidence ?? "—"})</span>
              <div style={{ whiteSpace: "pre-wrap" }}>{brief.model.reasoning || "no reasoning recorded"}</div></>
          : <span style={{ color: "var(--hs-text-muted)" }}>The model has not looked at this item.</span>}
      </div>
    </div>
  );
}

export default function ItemsPage({ styles, tierColors, Tag }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const f = useMemo(() => filtersFromParams(sp), [sp]);
  const setF = useCallback((nf) => setSp(new URLSearchParams(buildQuery(nf, null)), { replace: true }), [setSp]);
  const [cursor, setCursor] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, rule_ids: [] });
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [open, setOpen] = useState({});            // item id -> brief | null (loading)
  const [groupBy, setGroupBy] = useState("");
  const [groups, setGroups] = useState([]);
  const [note, setNote] = useState("");
  const [bulkRule, setBulkRule] = useState("");
  const [msg, setMsg] = useState("");

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setCursor(0); };
  const query = useMemo(() => buildQuery(f, cursor), [f, cursor]);

  const load = useCallback(() => api.items(query)
    .then((r) => { setData(r.data); setErr(""); })
    .catch((ex) => setErr(ex.message)), [query]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!groupBy) { setGroups([]); return; }
    const g = buildQuery({ ...f, publisher: groupBy === "publisher" ? "" : f.publisher, rule: groupBy === "rule" ? "" : f.rule }, null);
    api.itemGroups(groupBy, g).then((r) => setGroups(r.data.groups)).catch(() => setGroups([]));
  }, [groupBy, f]);

  const toggleOpen = async (it) => {
    if (it.id in open) { const { [it.id]: _gone, ...rest } = open; setOpen(rest); return; }
    setOpen({ ...open, [it.id]: null });
    try { const r = await api.itemBrief(it.id); setOpen((o) => ({ ...o, [it.id]: r.data })); }
    catch (ex) { setOpen((o) => ({ ...o, [it.id]: { rules: [], requires_secrets: [], model: null, error: ex.message } })); }
  };
  const toggleSel = (id) => { const s = new Set(selected); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };
  const allOnPage = data.items.length > 0 && data.items.every((i) => selected.has(i.id));
  const togglePage = () => {
    const s = new Set(selected);
    data.items.forEach((i) => (allOnPage ? s.delete(i.id) : s.add(i.id)));
    setSelected(s);
  };

  // Bulk labels are "benign" or "unsure" (like the Review page); "malicious" is per item only, as it makes things public.
  const bulkLabel = async (verdict) => {
    if (!window.confirm(`Label ${selected.size} item(s) ${verdict}? Each is recorded as a human verdict and audited.`)) return;
    try {
      const r = await api.bulkLabel({ item_ids: [...selected], verdict, note });
      setMsg(`Labelled ${r.data.labelled} ${verdict}${r.data.skipped.length ? `; skipped ${r.data.skipped.length} (${[...new Set(r.data.skipped.map((s) => s.reason))].join(", ")})` : ""}.`);
      setSelected(new Set()); setNote(""); load();
    } catch (ex) { setMsg("Error: " + ex.message); }
  };
  const bulkFeedback = async () => {
    try {
      const r = await api.bulkRuleFeedback({ item_ids: [...selected], rule_id: bulkRule || null, note });
      setMsg(`Recorded "rule too broad" for ${r.data.recorded} item(s)${r.data.skipped.length ? `; skipped ${r.data.skipped.length}` : ""}.`);
      setSelected(new Set()); setNote("");
    } catch (ex) { setMsg("Error: " + ex.message); }
  };
  const exportData = async () => {
    try {
      const blob = await api.exportReviewData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `herdsafe-review-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (ex) { window.alert("Export failed: " + ex.message); }
  };

  const from = data.total === 0 ? 0 : cursor + 1;
  const to = Math.min(cursor + PAGE_SIZE, data.total);
  const cols = 9;
  return (
    <div data-testid="items-page">
      <h2>Items</h2>
      <div style={{ ...styles.row, flexWrap: "wrap", gap: 8 }}>
        <input style={{ ...styles.input, width: 300 }} placeholder="search name, publisher, description, secret, URL, package, text"
               value={f.q} onChange={set("q")} data-testid="items-search" />
        <select data-testid="items-type-filter" style={styles.select} value={f.type} onChange={set("type")}>
          {TYPES.map((t) => <option key={t} value={t}>{t === "" ? "all types" : t}</option>)}
        </select>
        <select data-testid="items-tier-filter" style={styles.select} value={f.tier} onChange={set("tier")}>
          {TIERS.map((t) => <option key={t} value={t}>{t === "" ? "all tiers" : t}</option>)}
        </select>
        <select data-testid="items-severity-filter" style={styles.select} value={f.severity} onChange={set("severity")}>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s === "" ? "any severity" : `severity: ${s}`}</option>)}
        </select>
        <select data-testid="items-rule-filter" style={styles.select} value={f.rule} onChange={set("rule")}>
          <option value="">any rule</option>
          {data.rule_ids.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="items-llm-filter" style={styles.select} value={f.llm} onChange={set("llm")}>
          {LLM.map((l) => <option key={l} value={l}>{l === "" ? "any LLM verdict" : l === "none" ? "LLM: not asked" : `LLM: ${l}`}</option>)}
        </select>
        <input data-testid="items-publisher-filter" style={{ ...styles.input, width: 170 }} placeholder="publisher (exact)" value={f.publisher} onChange={set("publisher")} />
        <select data-testid="items-human-filter" style={styles.select} value={f.human} onChange={set("human")}>
          <option value="">human label: any</option><option value="yes">human-labelled</option><option value="no">not human-labelled</option>
        </select>
        <select data-testid="items-sort" style={styles.select} value={f.sort} onChange={set("sort")}>
          <option value="last_seen">newest first</option><option value="severity">worst severity first</option>
        </select>
        <select data-testid="items-groupby" style={styles.select} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          <option value="">no grouping</option><option value="publisher">group by publisher</option><option value="rule">group by rule</option>
        </select>
        <input data-testid="items-date-from" style={styles.input} type="date" value={f.dateFrom} onChange={set("dateFrom")} />
        <input data-testid="items-date-to" style={styles.input} type="date" value={f.dateTo} onChange={set("dateTo")} />
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", minHeight: 44 }}>
          <input type="checkbox" data-testid="items-reviewed-by-me" checked={f.reviewed_by_me === "true"}
                 onChange={(e) => { setF({ ...f, reviewed_by_me: e.target.checked ? "true" : "" }); setCursor(0); }} />reviewed by me</label>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", minHeight: 44 }}>
          <input type="checkbox" data-testid="items-has-my-note" checked={f.has_my_note === "true"}
                 onChange={(e) => { setF({ ...f, has_my_note: e.target.checked ? "true" : "" }); setCursor(0); }} />has my note</label>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", minHeight: 44 }}>
          <input type="checkbox" data-testid="items-too-large" checked={f.scan_status === "too_large"}
                 onChange={(e) => { setF({ ...f, scan_status: e.target.checked ? "too_large" : "" }); setCursor(0); }} />too large to scan</label>
        <select data-testid="items-capability-filter" aria-label="broad access" style={styles.select} value={f.capability} onChange={set("capability")}>
          <option value="">any access</option>
          {Object.entries(CAPABILITY_LABELS).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
        <button style={styles.btnDim} onClick={() => { setF(EMPTY); setCursor(0); }} data-testid="items-reset">Reset</button>
        <button data-testid="export-btn" style={styles.btnDim} onClick={exportData}>Export JSON (for rule tuning)</button>
      </div>

      {Object.keys(EXTRA_FILTERS).some((k) => f[k] && !["type", "capability", "reviewed_by_me", "has_my_note", "scan_status"].includes(k)) && (
        <div style={{ ...styles.row, fontSize: 12 }} data-testid="items-active-filters">
          {Object.entries(EXTRA_FILTERS).filter(([k]) => f[k] && !["type", "capability", "reviewed_by_me", "has_my_note", "scan_status"].includes(k)).map(([k, label]) => (
            <span key={k} data-testid={`filter-chip-${k}`} style={{ background: "var(--hs-surface-raised)", border: "1px solid var(--hs-border)", padding: "2px 8px", borderRadius: 999 }}>
              {label}{f[k] !== "true" && <>: <code>{f[k]}</code></>}
              <button aria-label={`remove ${label}`} style={{ ...styles.btnDim, padding: "0 6px", marginLeft: 6 }}
                      onClick={() => { setF({ ...f, [k]: "" }); setCursor(0); }}>×</button>
            </span>))}
        </div>)}
      {groupBy && (
        <div style={{ ...styles.card, maxHeight: 220, overflow: "auto" }} data-testid="items-groups">
          <strong>Groups by {groupBy}</strong> <span style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>(worst severity first; click to filter)</span>
          {groups.length === 0 && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>No groups for these filters.</div>}
          {groups.map((g) => (
            <div key={g.key} style={{ fontSize: 12, padding: "2px 0", cursor: "pointer" }} data-testid={`group-${g.key}`}
                 onClick={() => { setF({ ...f, [groupBy]: g.key }); setCursor(0); }}>
              <SeverityBadge severity={g.severity} /> <code>{g.key}</code> · {g.count} item{g.count === 1 ? "" : "s"}{g.threats > 0 && ` · ${g.threats} threat`}
            </div>))}
        </div>)}

      {err && <div style={{ ...styles.card, color: "var(--hs-danger)" }}>Could not load items: {err}</div>}
      {msg && <div style={{ ...styles.card, color: "var(--hs-accent)" }} data-testid="items-msg">{msg}</div>}
      {selected.size > 0 && (
        <div style={{ ...styles.card, position: "sticky", top: 0, zIndex: 2 }} data-testid="bulk-bar">
          <strong>{selected.size} selected</strong>{" "}
          <input data-testid="bulk-note" style={{ ...styles.input, width: 260 }} placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />{" "}
          <button data-testid="bulk-benign" style={styles.btn} onClick={() => bulkLabel("benign")}>Label benign</button>{" "}
          <button data-testid="bulk-unsure" style={styles.btnDim} onClick={() => bulkLabel("unsure")}>Label unsure</button>{" "}
          <select data-testid="bulk-rule" style={styles.select} value={bulkRule} onChange={(e) => setBulkRule(e.target.value)}>
            <option value="">each item's top rule</option>
            {data.rule_ids.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>{" "}
          <button data-testid="bulk-feedback" style={styles.btnDim} onClick={bulkFeedback}>Rule too broad</button>{" "}
          <button style={styles.btnDim} onClick={() => setSelected(new Set())}>Clear</button>
        </div>)}

      <table style={styles.tbl}>
        <thead><tr>
          <th style={styles.th}><input type="checkbox" aria-label="select page" checked={allOnPage} onChange={togglePage} data-testid="select-page" /></th>
          <th style={styles.th}></th><th style={styles.th}>Name</th><th style={styles.th}>Publisher</th><th style={styles.th}>Severity</th>
          <th style={styles.th}>Top signal</th><th style={styles.th}>LLM</th><th style={styles.th}>Needs secrets</th><th style={styles.th}>Last seen</th>
        </tr></thead>
        <tbody>{data.items.map((i) => {
          const secrets = i.requires_secrets || [];
          return (
            <React.Fragment key={i.id}>
              <tr data-testid={`item-row-${i.identifier}`}>
                <td style={styles.td}><input type="checkbox" aria-label={`select ${i.identifier}`} checked={selected.has(i.id)} onChange={() => toggleSel(i.id)} data-testid={`row-select-${i.id}`} /></td>
                <td style={styles.td}><button style={styles.btnDim} aria-expanded={i.id in open} aria-label={`details ${i.identifier}`} data-testid={`row-expand-${i.id}`} onClick={() => toggleOpen(i)}>{i.id in open ? "▾" : "▸"}</button></td>
                <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", cursor: "pointer" }} onClick={() => navigate(`/admin/items/${i.id}`)}>
                  {i.registry_name || i.display_name || i.identifier}
                  {(i.matched || []).length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--hs-accent)", fontFamily: "var(--hs-font-body)" }} data-testid={`item-matched-${i.identifier}`}>
                      matched: {i.matched.map((m, k) => <span key={k}>{k > 0 && " · "}<strong>{m.field}</strong>{m.value && m.field !== "name" && <> <span style={{ color: "var(--hs-text-muted)" }}>{m.value}</span></>}</span>)}
                    </div>)}
                  <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{i.type} · <Tag color={tierColors[i.current_tier] || "neutral"}>{i.current_tier}</Tag>{i.has_human_label && ` · human: ${i.human_label}`}{i.open_report_count > 0 && ` · ${i.open_report_count} report(s)`}
                    {i.scan_status === "too_large" && <> · <span className="hs-badge hs-badge-warning" title={i.scan_status_detail || ""}
                      data-testid={`item-too-large-${i.identifier}`}>too large to scan</span></>}</div>
                </td>
                <td style={{ ...styles.td, fontSize: 12 }} data-testid={`item-publisher-${i.identifier}`}>{i.publisher || "—"}</td>
                <td style={styles.td}><SeverityBadge severity={i.severity} /></td>
                <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }} data-testid={`item-toprule-${i.identifier}`}>{i.top_rule || "—"}</td>
                <td style={{ ...styles.td, fontSize: 12, color: LLM_COLORS[i.llm_label] || "var(--hs-text-muted)" }}>{i.llm_label || "—"}</td>
                <td style={{ ...styles.td, fontSize: 12 }} title={secrets.map((s) => `${s.name} (${s.type})`).join("\n")}>
                  {secrets.length === 0 ? "—" : <>{secrets.slice(0, 2).map((s) => s.name).join(", ")}{secrets.length > 2 && ` +${secrets.length - 2}`}</>}
                  {(i.capabilities || []).length > 0 && <div style={{ marginTop: 4 }}><CapabilityBadges capabilities={i.capabilities} testid={`item-caps-${i.identifier}`} /></div>}
                  {(i.payment_authorization || []).length > 0 && (
                    <div data-testid={`item-payment-${i.identifier}`}>payment: <code>{i.payment_authorization.join(", ")}</code></div>)}
                </td>
                <td style={styles.td}><Time value={i.last_seen_at} /></td>
              </tr>
              {i.id in open && (
                <tr data-testid={`row-detail-${i.id}`}><td style={styles.td} colSpan={cols}><Brief brief={open[i.id]} /></td></tr>)}
            </React.Fragment>);
        })}</tbody>
      </table>
      <div style={{ ...styles.row, marginTop: 8 }} data-testid="items-pager">
        <button style={styles.btnDim} disabled={cursor === 0} onClick={() => setCursor(Math.max(0, cursor - PAGE_SIZE))} data-testid="items-prev">← Prev</button>
        <span data-testid="items-range" style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{from}–{to} of {data.total}</span>
        <button style={styles.btnDim} disabled={to >= data.total} onClick={() => setCursor(cursor + PAGE_SIZE)} data-testid="items-next">Next →</button>
      </div>
    </div>
  );
}
